import { useEffect, useState } from 'react';
import { hasCanvas } from '../../utils/canvasSchema';
import { fetchWorksheetCanvasOutline } from '../../utils/worksheetCanvasFetch';
import {
  applyPaperFormatToOutline,
  paperFormatFromOutline,
} from '../../utils/worksheetPaperFormat';
import { getTheme, DEFAULT_THEME_ID } from '../../utils/canvasThemes';

const PAGE_W = 794;
const PAGE_H = 1123;

const canvasSlides = (outline) => {
  const slides = outline?.slides;
  if (!Array.isArray(slides) || slides.length === 0) return [];
  if (!slides.every((slide) => hasCanvas(slide))) return [];
  return slides;
};

const hasWorksheetBody = (outline) => (
  (Array.isArray(outline?.slides) && outline.slides.length > 0)
  || (Array.isArray(outline?.questions) && outline.questions.length > 0)
  || (Array.isArray(outline?.sections) && outline.sections.length > 0)
);

const asText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  return '';
};

const localTextbox = (id, text, x, y, w, h, style, role) => ({
  id,
  type: 'textbox',
  x,
  y,
  w,
  h,
  rotate: 0,
  z: 2,
  text,
  style,
  semantic: { role },
});

/** API가 실패해도 문항/섹션으로 A4 쪽 썸네일을 남긴다. */
export function buildLocalStudentSlides(outline) {
  const questions = Array.isArray(outline?.questions) ? outline.questions : [];
  const sections = Array.isArray(outline?.sections) ? outline.sections : [];
  const entries = questions.length
    ? questions.map((question, index) => ({
      number: question?.number || index + 1,
      title: asText(question?.question || question?.title) || `${index + 1}번`,
    }))
    : sections.map((section, index) => ({
      number: section?.section_number || index + 1,
      title: asText(section?.title) || `${index + 1}번 활동`,
    }));
  if (!entries.length) return [];
  const theme = getTheme(outline?.canvas_theme || DEFAULT_THEME_ID);
  const perPage = 6;
  const pages = [];
  for (let start = 0; start < entries.length && pages.length < 30; start += perPage) {
    const chunk = entries.slice(start, start + perPage);
    const objects = [
      localTextbox(
        `ws_local_title_${pages.length}`,
        asText(outline?.title) || '학습지',
        48,
        40,
        PAGE_W - 96,
        36,
        {
          fontSize: 22,
          fontWeight: 800,
          color: theme.colors.accent,
          fontFamily: 'Pretendard, system-ui, sans-serif',
        },
        'worksheet-title',
      ),
    ];
    chunk.forEach((entry, index) => {
      objects.push(localTextbox(
        `ws_local_q_${pages.length}_${index}`,
        `${entry.number}. ${entry.title}`,
        48,
        100 + index * 150,
        PAGE_W - 96,
        130,
        {
          fontSize: 16,
          fontWeight: 550,
          color: theme.colors.text1,
          lineHeight: 1.45,
          fontFamily: 'Pretendard, system-ui, sans-serif',
        },
        'worksheet-prompt',
      ));
    });
    pages.push({
      slide_id: `worksheet_student_${pages.length + 1}`,
      slide_number: pages.length + 1,
      page_kind: 'student',
      title: pages.length === 0 ? (asText(outline?.title) || '1쪽') : `${pages.length + 1}쪽`,
      canvas: {
        width: PAGE_W,
        height: PAGE_H,
        background: theme.colors.bg,
        objects,
      },
    });
  }
  return pages;
}

/**
 * 채팅 텍스트 학습지와 같은 경로: layout_hint를 존중한 뒤
 * buildWorksheetCanvas → HtmlSlideRenderer가 그릴 쪽.
 * 이미 캔버스 쪽이 있으면 API를 부르지 않는다.
 */
export default function useHydratedWorksheetOutline(outline, key) {
  const [built, setBuilt] = useState(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!outline || !hasWorksheetBody(outline)) {
      setBuilt(null);
      setPending(false);
      return () => { cancelled = true; };
    }
    if (canvasSlides(outline).length > 0) {
      setBuilt(outline);
      setPending(false);
      return () => { cancelled = true; };
    }
    setPending(true);
    const format = paperFormatFromOutline(outline);
    const outlineForBuild = applyPaperFormatToOutline(outline, format);
    Promise.resolve(fetchWorksheetCanvasOutline({
      outline: outlineForBuild,
      worksheetStyle: outlineForBuild.worksheet_style,
    }))
      .then((result) => {
        if (cancelled) return;
        const slides = result?.success ? canvasSlides(result.outline) : [];
        if (slides.length) {
          setBuilt(result.outline);
          return;
        }
        const local = buildLocalStudentSlides(outline);
        setBuilt(local.length ? { ...outline, slides: local } : null);
      })
      .catch(() => {
        if (cancelled) return;
        const local = buildLocalStudentSlides(outline);
        setBuilt(local.length ? { ...outline, slides: local } : null);
      })
      .finally(() => {
        if (!cancelled) setPending(false);
      });
    return () => { cancelled = true; };
  }, [outline, key]);

  const ready = canvasSlides(built);
  const immediate = canvasSlides(outline);
  const slides = ready.length ? ready : immediate;
  return { outline: built || outline || null, slides, pending };
}
